import { AlertTriangle, Monitor, Pencil, Plus, Store as StoreIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import Modal from '../../../../components/shared/Modal/Modal';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, editAction, viewAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import { DetailCard, DetailField, DetailFieldList, DetailIdentity } from '../../../../components/shared/DetailPanel/DetailBlocks';
import { PosTerminalsPanel } from './PosTerminalsPanel';
import { useToast } from '../../../../hooks/useToast';
import { listBillingLines } from '../../../../services/BillingLinesService';
import { listCostCenters } from '../../../../services/CostCentersService';
import { createStore, deleteStore, listStores, updateStore } from '../../../../services/StoresService';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { listWarehouses } from '../../../../services/WarehousesService';
import { CostCenter } from '../../../../types/CostCenter.types';
import { BillingLine, Store } from '../../../../types/Store.types';
import { Warehouse } from '../../../../types';
import '../shared-modules.css';

/** Lo que le falta a la tienda para vender bien; los TPV venden igual, pero con estas consecuencias. */
const configWarnings = (warehouseId: string, billingLineId: string): string[] => {
  const out: string[] = [];
  if (!warehouseId) out.push('Sin almacén: las ventas de sus TPV no descuentan stock.');
  if (!billingLineId) out.push(`Sin serie de facturación: sus tickets saldrán con la serie ${warehouseId ? 'T<id del almacén>' : 'T (compartida con otros TPV sin almacén)'} y sus facturas con ${warehouseId ? 'F<id del almacén>' : 'F'}.`);
  return out;
};

/** Tiendas del canal Librerías: almacén, línea de facturación y centro de coste; los TPV cuelgan de la tienda. */
export const StoresPanel = () => {
  const { notify } = useToast();
  const [stores, setStores] = useState<Store[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [billingLines, setBillingLines] = useState<BillingLine[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [editing, setEditing] = useState<Store | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [billingLineId, setBillingLineId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [status, setStatus] = useState<Store['status']>('active');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Store | null>(null);

  const reload = () => listStores().then(setStores).catch(err => console.error('Error fetching stores:', err));

  useEffect(() => {
    reload();
    let companyId = '';
    try { companyId = getActiveCompanyId(); } catch { companyId = ''; }
    listWarehouses().then(setWarehouses).catch(err => console.error('Error fetching warehouses:', err));
    listBillingLines().then(setBillingLines).catch(err => console.error('Error fetching billing lines:', err));
    listCostCenters({ companyId: companyId || undefined }).then(setCostCenters).catch(err => console.error('Error fetching cost centers:', err));
  }, []);

  const openForm = (store?: Store) => {
    setEditing(store || null);
    setCode(store?.code || '');
    setName(store?.name || '');
    setAddress(store?.address || '');
    setPhone(store?.phone || '');
    setEmail(store?.email || '');
    setWarehouseId(store?.warehouseId || '');
    setBillingLineId(store?.billingLineId || '');
    setCostCenterId(store?.costCenterId || '');
    setStatus(store?.status || 'active');
    setFormError('');
    setFormOpen(true);
  };

  const selected = stores.find(st => st.id === selectedId) || null;

  const handleSave = async () => {
    if (!editing && !code.trim()) { setFormError('El código es obligatorio.'); return; }
    if (!name.trim()) { setFormError('El nombre es obligatorio.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        code: code.trim(), name: name.trim(), address: address.trim(), phone: phone.trim(), email: email.trim(),
        warehouseId, billingLineId, costCenterId, status
      };
      if (editing) await updateStore(editing.id, payload);
      else await createStore(payload);
      notify('Tiendas', editing ? 'Tienda actualizada.' : 'Tienda creada y registrada en el canal Librerías.', 'info');
      await reload();
      setFormOpen(false);
    } catch (err) {
      setFormError((err as Error)?.message || 'Error al guardar la tienda.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteStore(target.id);
      notify('Tiendas', `Tienda ${target.code} eliminada.`, 'info');
      await reload();
    } catch (err) {
      notify('Tiendas', (err as Error)?.message || 'Error al eliminar la tienda.', 'warning');
    }
  };

  const columns: ColumnDef<Store>[] = [
    { id: 'code', header: 'CÓDIGO', render: (s) => <span className="sales-channel-entity-id">{s.code}</span> },
    { id: 'name', header: 'TIENDA', render: (s) => <span>{s.name}</span> },
    { id: 'warehouse', header: 'ALMACÉN', render: (s) => <span>{s.warehouseName || 'Sin asignar'}</span> },
    { id: 'billing', header: 'SERIES (TICKET / FACTURA)', render: (s) => <span>{s.billingLineName ? `${s.billingLineName} (${s.billingSeries} / ${billingLines.find(l => l.id === s.billingLineId)?.invoiceSeries || 'F'})` : 'Sin asignar'}</span> },
    { id: 'cost', header: 'CENTRO DE COSTE', render: (s) => <span>{s.costCenterName || 'Sin asignar'}</span> },
    {
      id: 'warn', header: '', render: (s) => {
        const w = configWarnings(s.warehouseId, s.billingLineId);
        return w.length > 0 ? <span title={w.join(' ')} style={{ color: '#fbbf24' }}><AlertTriangle size={14} /></span> : null;
      }
    },
    { id: 'pos', header: 'TPV', render: (s) => <span>{s.posCount}</span> },
    { id: 'status', header: 'ESTADO', render: (s) => <span className={`module-badge ${s.status === 'active' ? 'active' : 'pending'}`}>{s.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span> }
  ];

  return (
    <>
      <div className="module-toolbar">
        <button type="button" className="module-action-btn" onClick={() => openForm()} data-keep-detail>
          <Plus size={12} /> Nueva tienda
        </button>
      </div>

      <MasterDetailLayout isOpen={formOpen || Boolean(selected)}>
        <DataTable
          data={stores}
          columns={columns}
          title={<><StoreIcon size={14} className="module-panel-title-icon" /> Tiendas</>}
          subtitle={`TOTAL: ${stores.length}`}
          getRowId={(s) => s.id}
          onRowClick={(s) => { setFormOpen(false); setSelectedId(s.id); }}
          selectedRowId={selected ? selected.id : null}
          emptyText="Ninguna tienda registrada"
          actionsHeader="ACCIONES"
          actionsColumn={(s) => (
            <RowActionsMenu
              actions={[
                viewAction(() => { setFormOpen(false); setSelectedId(s.id); }),
                editAction(() => openForm(s)),
                deleteAction(() => setDeleteTarget(s))
              ]}
            />
          )}
        />

        {!formOpen && selected && (
          <DetailPanel
            title="Ficha_De_Tienda"
            expandable
            onClose={() => setSelectedId('')}
            headerActions={
              <button type="button" className="module-action-btn" onClick={() => openForm(selected)} data-keep-detail>
                <Pencil size={12} /> Editar
              </button>
            }
            cards={
              <DetailCard title="Terminales_TPV" icon={Monitor}>
                <PosTerminalsPanel storeId={selected.id} onChanged={reload} />
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={StoreIcon}
              reference={selected.code}
              name={selected.name}
              subtitle={<span className={`module-badge ${selected.status === 'active' ? 'active' : 'pending'}`}>{selected.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span>}
            />
            {configWarnings(selected.warehouseId, selected.billingLineId).map(w => (
              <div key={w} className="module-form-error"><span><AlertTriangle size={12} /> {w}</span></div>
            ))}
            <DetailFieldList>
              <DetailField label="Dirección">{selected.address || '—'}</DetailField>
              <DetailField label="Teléfono">{selected.phone || '—'}</DetailField>
              <DetailField label="Email">{selected.email || '—'}</DetailField>
              <DetailField label="Almacén">{selected.warehouseName || 'Sin asignar'}</DetailField>
              <DetailField label="Serie de tickets">{selected.billingLineName ? selected.billingSeries : 'Sin asignar'}</DetailField>
              <DetailField label="Serie de facturas">{selected.billingLineName ? (billingLines.find(l => l.id === selected.billingLineId)?.invoiceSeries || 'F + almacén') : 'Sin asignar'}</DetailField>
              <DetailField label="Centro de coste">{selected.costCenterName || 'Sin asignar'}</DetailField>
            </DetailFieldList>
          </DetailPanel>
        )}

        {formOpen && (
          <SidebarForm
            title={editing ? 'Editar_Tienda' : 'Alta_Tienda'}
            expandable
            onClose={() => setFormOpen(false)}
            submitLabel={saving ? 'Guardando...' : editing ? 'Guardar' : 'Crear tienda'}
            submitDisabled={saving}
            onSubmit={handleSave}
          >
            {formError && <div className="module-form-error"><span>{formError}</span></div>}
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Código *</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} disabled={Boolean(editing)} className="sidebar-form-input" placeholder="T-001" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Nombre *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Dirección</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Teléfono</label>
              <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Email</label>
              <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} className="sidebar-form-input" />
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Almacén</label>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="sidebar-form-input">
                <option value="">Sin asignar</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Serie de facturación</label>
              <select value={billingLineId} onChange={(e) => setBillingLineId(e.target.value)} className="sidebar-form-input">
                <option value="">Sin asignar</option>
                {billingLines.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name} (tickets {l.series} · facturas {l.invoiceSeries || 'F'})</option>)}
              </select>
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Centro de coste</label>
              <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)} className="sidebar-form-input">
                <option value="">Sin asignar</option>
                {costCenters.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
              </select>
            </div>
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Estado</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as Store['status'])} className="sidebar-form-input">
                <option value="active">ACTIVA</option>
                <option value="inactive">INACTIVA</option>
              </select>
            </div>
            {configWarnings(warehouseId, billingLineId).map(w => (
              <div key={w} className="module-form-error"><span><AlertTriangle size={12} /> {w}</span></div>
            ))}
            <p style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)' }}>
              Los TPV de la tienda usan su almacén y facturan con la serie elegida.
            </p>
          </SidebarForm>
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar tienda"
        message={`¿Eliminar la tienda "${deleteTarget?.code}"? Sus TPV quedarán sin tienda.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};
